import { computed, inject, Injectable, OnDestroy, Signal } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { catchError, takeUntil } from 'rxjs/operators';
import { forkJoin, of, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  DisplayInvitationGroup,
  InvitationReceivedExtended,
  InvitationsState,
  PrimeInviteGCPayload,
  PrimeInviteManagerPayload,
} from './invitations.types';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { NotificationV2Service } from '@app/notification-v2.service';
import { InvitesApiService } from '@app/services/invites-api.service';
import { isTextPresentInValues } from '@app/utils/isTextPresentInValues';
import { stringToInitials } from '@app/utils/stringToIntials';
import { ProjectDto } from '@app/pages/webapp/projects/projects.interface';
import { CurrentUserService } from '@app/services/current-user.service';
import { INTERACTION_BAR_STATES } from '@app/framework/constants/interaction-bar.constants';
import { ROUTE_WEBAPP } from '@app/framework/constants/route.webapp.constants';
import { primeSummaryActions } from '@app/pages/webapp/projects/view-project/add-panel-view/prime-commitment-view/prime-contract-summary.actions';

@Injectable({ providedIn: 'root' })
export class InvitationsStore implements OnDestroy {
  readonly user = inject(CurrentUserService);
  readonly notif = inject(NotificationV2Service);
  readonly store = inject(Store);
  readonly interactionBarStateService = inject(InteractionBarStateService);
  readonly invitesApiService = inject(InvitesApiService);
  readonly userService = inject(CurrentUserService);
  readonly router = inject(Router);
  private isDestroyed$ = new Subject();

  private readonly state = signalState<InvitationsState>({
    isLoading: false,
    invitationGroups: [],
    searchText: null,
    invitationsReceived: [],
    invitationBeingAccepted: null,
  });

  public invitationGroups: Signal<DisplayInvitationGroup[]> = computed(() => {
    const searchText = this.state.searchText();
    return this.state
      .invitationGroups()
      .map((inviteGroup): DisplayInvitationGroup => {
        console.log('inviteGroup', inviteGroup);
        const groupNameMatches = isTextPresentInValues(searchText, [inviteGroup.title]);
        return {
          id: inviteGroup.id,
          name: inviteGroup.title,
          initials: stringToInitials(inviteGroup.title),
          invitations: [
            ...inviteGroup.prime_project_invitations.map((invitation) => ({
              ...invitation,
              isPrime: true,
            })),
            ...inviteGroup.sub_project_invites.map((invitation) => ({
              ...invitation,
              isPrime: false,
            })),
          ]
            .map((invitation) => {
              console.log('invitation', invitation);
              const contact =
                invitation?.sp_contact || invitation?.manager_contact || invitation?.contact;
              return {
                ...invitation,
                id: invitation.id,
                email: contact?.email,
                name: contact ? `${contact?.first_name} ${contact?.last_name}` : 'Unknown',
                title: contact?.title,
                status: invitation.status,
                initials: (contact?.first_name?.[0] ?? 'U') + (contact?.last_name?.[0] ?? 'K'),
                isPrime: invitation.isPrime,
              };
            })
            .filter((invitation) => {
              const contact = invitation?.sp_contact || invitation?.manager_contact;
              const invitationMatches = contact
                ? isTextPresentInValues(searchText, [
                    `${contact.first_name} ${contact.last_name}`,
                    contact.email,
                    contact.title,
                    contact.phone,
                    contact.address,
                    contact.city,
                    contact.state,
                    contact.zip,
                  ])
                : true;
              return groupNameMatches || invitationMatches;
            }),
        };
      })
      .filter((group) => {
        const groupNameMatches = isTextPresentInValues(searchText, [group.name]);
        return groupNameMatches || group.invitations.length > 0;
      });
  });
  public isLoading = this.state.isLoading;
  public searchText = this.state.searchText;
  public invitationsReceived = this.state.invitationsReceived;
  public invitationBeingAccepted = this.state.invitationBeingAccepted;

  setLoading(isLoading: boolean) {
    patchState(this.state, { isLoading });
  }

  setSearchText(text: string) {
    patchState(this.state, { searchText: text });
  }

  public loadInvitationsSent() {
    this.setLoading(true);
    this.invitesApiService
      .getInvitationsSent()
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (invites) => {
          patchState(this.state, { invitationGroups: invites, isLoading: false });
        },
        error: (err: HttpErrorResponse) => {
          console.error('Failed to load invites', err);
          if (err.status === 500) {
            this.notif.showError('Our servers encountered an error. Please come back later.');
          } else {
            this.notif.showError('Failed to load invites.');
          }
          this.setLoading(false);
        },
      });
  }

  /**
   * Managers cannot receive subcontract invitations, so we skip that call for them.
   */
  public loadAllInvitationsReceived() {
    this.setLoading(true);

    const subContractInvites$ = this.user.isManager
      ? of([])
      : this.invitesApiService.getSubcontractInvitesReceived().pipe(
          catchError((error) => {
            console.error(error);
            this.notif.showError('Failed to load subcontract invitations.');
            return of([]);
          }),
        );

    const primeInvites$ = this.user.isFreeSP
      ? of([])
      : this.invitesApiService.getPrimeInvitationsReceived().pipe(
          catchError((error) => {
            console.error(error);
            this.notif.showError('Failed to load prime invitations.');
            return of([]);
          }),
        );

    forkJoin([subContractInvites$, primeInvites$])
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: ([subcontractInvites, primeInvites]) => {
          patchState(this.state, {
            invitationsReceived: [
              ...subcontractInvites.map((invite) => ({ ...invite, isPrime: false })),
              ...primeInvites.map((invite) => ({ ...invite, isPrime: true })),
            ],
            isLoading: false,
          });
        },
        error: (err) => {
          console.error('Failed to load invites', err);
          this.notif.showError('Failed to load invites.');
          this.setLoading(false);
        },
      });
  }

  public sendSubcontractInvite(payload: { project_id: number; contact_id: number }) {
    this.setLoading(true);
    this.invitesApiService
      .sendSubcontractInvite(payload)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (invitation) => {
          this.notif.showSuccess('Invitation sent successfully.');
          this.loadInvitationsSent();
          this.interactionBarStateService.close();
        },
        error: (err: { error: { message: string } }) => {
          this.notif.showError(err.error.message || 'Failed to send invite.');
          this.setLoading(false);
        },
      });
  }

  public sendPrimeInvite(
    payload: PrimeInviteGCPayload | PrimeInviteManagerPayload,
    markAsSubmitted = false,
  ) {
    this.setLoading(true);
    this.invitesApiService
      .sendPrimeInvite(payload)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: () => {
          this.notif.showSuccess('Invitation sent successfully.');
          if (markAsSubmitted) {
            this.store.dispatch(primeSummaryActions.sendPrimeContract());
          }
          this.loadInvitationsSent();
          this.interactionBarStateService.close();
        },
        error: (err: { error: { message: string } }) => {
          this.notif.showError(err.error.message || 'Failed to send invite.');
          this.setLoading(false);
        },
      });
  }

  public resendSubcontractInvite(inviteId: string) {
    this.setLoading(true);
    this.invitesApiService
      .resendSubcontractInvite(inviteId)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (invitation) => {
          this.notif.showSuccess('Invitation resent successfully.');
          this.loadInvitationsSent();
          this.interactionBarStateService.close();
        },
        error: (err) => {
          console.error('Failed to resend invite', err);
          this.notif.showError('Failed to resend invite.');
          this.setLoading(false);
        },
      });
  }

  public resendPrimeInvite(inviteId: string) {
    this.setLoading(true);
    this.invitesApiService
      .resendPrimeInvite(inviteId)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (invitation) => {
          this.notif.showSuccess('Invitation resent successfully.');
          this.loadInvitationsSent();
          this.interactionBarStateService.close();
        },
        error: (err) => {
          console.error('Failed to resend invite', err);
          this.notif.showError('Failed to resend invite.');
          this.setLoading(false);
        },
      });
  }

  /**
   * Handles accepting an invitation based on user type:
   * 1. If the user is Premium Service Provider, opens the project setup sidebar to create a new project.
   * 2. If the user is a Free Subcontractor, directly accepts the invitation.
   * @param invitation
   */
  public acceptInviteFirstStep(invitation: InvitationReceivedExtended) {
    if (this.user.isFreeSP) {
      this.acceptInvite(invitation);
      return;
    }

    const sidebarState = this.userService.isManager
      ? INTERACTION_BAR_STATES.SETUP_PROJECT_MANAGER
      : INTERACTION_BAR_STATES.SETUP_PROJECT_GC;
    this.interactionBarStateService.openProjectSetup(sidebarState, {
      isInvitationAccept: true,
      isEdit: false,
    });

    patchState(this.state, { invitationBeingAccepted: invitation });
  }

  /**
   * Calls the appropriate API to accept the invitation based on its type (Prime or Subcontract).
   * @param invitation
   * @param newProject may be null if Subcontractor accepts the invitation - no need to create a new project
   */
  public acceptInvite(invitation: InvitationReceivedExtended, newProject?: Partial<ProjectDto>) {
    if (!invitation) {
      this.notif.showError('No invitation selected for acceptance.');
      return;
    }

    if (invitation.isPrime) {
      this.acceptPrimeInvite(invitation.id, newProject);
    } else {
      this.acceptSubcontractInvite(invitation.id, newProject);
    }
  }

  /**
   * Calls the appropriate API to dismiss the invitation based on its type (Prime or Subcontract).
   * An invitation can be dismissed only if it is pending.
   * @param invitation
   */
  public dismissInvite(invitation: { id: string; isPrime: boolean } | null) {
    if (!invitation) {
      this.notif.showError('No invitation selected for acceptance.');
      return;
    }

    if (invitation.isPrime) {
      this.dismissPrimeInvite(invitation.id);
    } else {
      this.dismissSubcontractInvite(invitation.id);
    }
  }

  private dismissPrimeInvite(inviteId: string) {
    this.setLoading(true);
    this.invitesApiService
      .dismissPrimeInvite(inviteId)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: () => {
          this.notif.showSuccess('Invitation dismissed successfully.');
          this.loadAllInvitationsReceived();
        },
        error: (err) => {
          console.error('Failed to dismiss invite', err);
          this.notif.showError('Failed to dismiss invite.');
          this.setLoading(false);
        },
      });
  }

  private dismissSubcontractInvite(inviteId: string) {
    this.setLoading(true);
    this.invitesApiService
      .dismissSubcontractInvite(inviteId)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: () => {
          this.notif.showSuccess('Invitation dismissed successfully.');
          this.loadAllInvitationsReceived();
        },
        error: (err) => {
          console.error('Failed to dismiss invite', err);
          this.notif.showError('Failed to dismiss invite.');
          this.setLoading(false);
        },
      });
  }

  /**
   * Cancels a previously sent Prime invitation. An invitation can be cancelled in any status, but only by the sender.
   * @param invitationId
   */
  public cancelSentPrimeInvitation(invitationId: string) {
    this.setLoading(true);
    this.invitesApiService
      .cancelSentPrimeInvite(invitationId)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: () => {
          this.notif.showSuccess('Invitation cancelled successfully.');
          this.loadInvitationsSent();
        },
        error: (err) => {
          console.error('Failed to cancel invite', err);
          this.notif.showError('Failed to cancel invite.');
          this.setLoading(false);
        },
      });
  }

  /**
   * Cancels a previously sent Prime invitation. An invitation can be cancelled in any status, but only by the sender.
   */
  public cancelSentSubcontractInvitation(invitationId: string) {
    this.setLoading(true);
    this.invitesApiService
      .cancelSentSubcontractInvite(invitationId)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: () => {
          this.notif.showSuccess('Invitation cancelled successfully.');
          this.loadInvitationsSent();
        },
        error: (err) => {
          console.error('Failed to cancel invite', err);
          this.notif.showError('Failed to cancel invite.');
          this.setLoading(false);
        },
      });
  }

  /**
   * Handles the API call to accept a Prime invitation. It sends the new project data along with the acceptance.
   */
  private acceptPrimeInvite(inviteId: string, newProject: Partial<ProjectDto>) {
    this.setLoading(true);
    this.invitesApiService
      .acceptPrimeInvite(inviteId, newProject)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (newProject) => {
          this.notif.showSuccess('Invitation accepted successfully.');
          this.loadAllInvitationsReceived();
          this.router.navigate([ROUTE_WEBAPP.BASE, ROUTE_WEBAPP.PROJECTS, newProject?.id]);
          this.interactionBarStateService.close();
        },
        error: (err) => {
          console.error('Failed to accept invite', err);
          this.notif.showError('Failed to accept invite.');
          this.setLoading(false);
        },
      });
  }

  /**
   * Handles the API call to accept a Subcontract invitation.
   * @param inviteId
   * @param newProject optional, may be null if Subcontractor accepts the invitation - no need to create a new project
   * @private
   */
  private acceptSubcontractInvite(inviteId: string, newProject?: Partial<ProjectDto>) {
    this.setLoading(true);
    let loadingHash = null;
    if (!newProject) {
      loadingHash = this.notif.showLoading();
    }
    this.invitesApiService
      .acceptSubcontractInvite(inviteId, newProject)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (newProject) => {
          this.notif.close(loadingHash);
          this.notif.showSuccess('Invitation accepted successfully.');
          this.loadAllInvitationsReceived();
          if (newProject) {
            this.router.navigate([ROUTE_WEBAPP.BASE, ROUTE_WEBAPP.PROJECTS, newProject.id]);
          }
          this.interactionBarStateService.close();
        },
        error: (err) => {
          this.notif.close(loadingHash);
          console.error('Failed to accept invite', err);
          this.notif.showError('Failed to accept invite.');
          this.setLoading(false);
        },
      });
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }
}
